import { readCoSubstrate, writeCoSubstrate, type CoSubstrate } from './reaction-cosubstrate';
import { toDisplayMapped, toSourceMapped } from './reaction-display';
import {
  applyMark, clearMark, parseMarks, serializeMarks, shiftMarks, sliceMarks, snapRange, tidyMarks,
  type Mark, type MarkKind,
} from './reaction-marks';

/**
 * A REACTION AS A ROW OF TOKENS, WITH MARKS ON RANGES INSIDE THEM.
 *
 * ── WHY TOKENS WORK HERE AND A TREE WOULD BE NEEDED FOR MATHS ─────────────────────────────────
 * An equation nests arbitrarily — a fraction inside an exponent inside a radical — which is why
 * editing one takes something the size of MathLive. A reaction does not nest: it is a flat run of
 * species and operators separated by spaces, and that is mhchem's own rule rather than a
 * convention we are imposing. `chem-formula-extension.ts` already relies on it to let a reaction
 * wrap, splitting on spaces at bracket depth zero and noting that a `+` with spaces around it is
 * an operator while one attached to a formula (`Na+`, `Ca^2+`) is a charge.
 *
 * So the editing surface can be a row of tokens over the same mhchem string the renderer, the
 * wrapper and `reaction-speech.ts` already read. The stored format does not change — which is not
 * a nicety: the speech module parses mhchem source to build each reaction's accessible name,
 * because KaTeX's MathML for mhchem reads out as "H X 2 O".
 *
 * ── COLOUR IS PER TOKEN, AND THAT IS A LIMIT OF THE NOTATION, NOT A SIMPLIFICATION ────────────
 * A span covering PART of a token was built first and then removed, because the thing it existed
 * to allow cannot be drawn. Measured, in every form mhchem offers:
 *
 *   Colouring a whole species                   `[c:CO2]`        subscript survives.
 *   Colouring a fragment that owns its subscript `2[c:H2]O`      survives, via mhchem's `$…$`.
 *   Colouring `CO` and `2` separately            `[a:CO][b:2]`   IMPOSSIBLE.
 *
 * mhchem makes a digit a subscript by meeting it as a bare digit. Colouring it means wrapping it,
 * and a wrapped digit is not one any more — so the subscript goes, at any price, by any route.
 * Since the interesting partial case is the one that cannot work, colour belongs to the token.
 *
 * Colour is also lifted OUT of the text. It is stored as Agavi's own `[#rrggbb:…]`, and the old
 * editor put that straight into the box the teacher was typing in: colouring H2 left them looking
 * at `2[#A324BA:H2] + O2 -> 2H2O`. Here the text is the chemistry and the colour rides alongside.
 *
 * ── WHAT IS DELIBERATELY LEFT AS TEXT ─────────────────────────────────────────────────────────
 * A colour that does not cover its whole token — which older content may hold — stays literal.
 * It still RENDERS exactly as it did, since the renderer's own handling is untouched; it simply
 * is not lifted into an editable colour. Promoting it to the whole token would silently repaint
 * characters the teacher did not choose, and dropping it would lose their work.
 *
 * A named colour (`[red:…]`) stays literal for a second reason: mhchem's annotated arrow
 * `->[Fe:cat]` is bracket-then-word-then-colon too, and reading a catalyst label as a colour
 * would be worse than leaving a hand-typed colour as text. The colour button has only ever
 * written hex.
 *
 * Whitespace between tokens normalises to one space, which is what the renderer does when it
 * rejoins them anyway.
 */

/**
 * One species, operator or arrow — what a cursor lives inside, and what marks apply within.
 *
 * `color` on the whole token is gone. A mark is a RANGE now, because the notation allows far
 * more than a whole token: only a boundary falling between an atom and the digits that subscript
 * it actually breaks, and `snapRange` moves those. See `reaction-marks.ts`.
 */
export type Token = {
  text: string;
  marks?: Mark[];
  /**
   * Present when this token DRAWS a molecule instead of being mhchem text.
   *
   * `text` stays the source either way, which is what keeps the row's editing model intact: a
   * focused token shows its source in a field, and for a structure that source is its SMILES.
   * The name rides along because nothing can derive it — OpenChemLib has no IUPAC naming — and
   * it is what a screen reader says, so a structure without one would be unspeakable.
   */
  structure?: { name: string; arrows?: string; annotations?: string };
  /**
   * Present when this token is a CO-SUBSTRATE ARC rather than mhchem text.
   *
   * Like a structure, it is drawn rather than typeset, so it leaves the KaTeX run and is laid
   * beside it — see `reaction-compose`. `text` stays the source, as it does for a structure.
   */
  cosub?: CoSubstrate;
};

/**
 * `[smiles:<SMILES>|<name>]`, and optionally `|<arrows>` and `|<annotations>`.
 *
 * SMILES never contains a pipe, so the separator is unambiguous. The third field is the curly
 * arrows and the fourth is everything a teacher has written ONTO the drawing — absent on every
 * structure written before each existed, which is why they are optional rather than new token
 * kinds. A reaction with annotations and no arrows carries the empty third field rather than
 * moving the fourth up: the fields are positional, and a reader cannot tell which one it has.
 */
/*
 * The SMILES may contain SQUARE BRACKETS, which is why this does not try to match the fields
 * with one pattern. `[OH-]`, `[N+](=O)[O-]` and `[C@H]` are ordinary SMILES — a charge, the
 * builder's own nitro group, a stereocentre — and 24 of the 122 library entries have one,
 * including every amino acid. Excluding `]` from the SMILES made all of them fail to parse, so
 * they drew correctly while being authored (the node is built from attributes) and came back as
 * raw text the moment the reaction was written out and read again.
 *
 * SMILES has no `|`, so the fields are split on that and the token simply ends at its last `]`.
 */
const STRUCTURE_WRAP = /^\[smiles:([\s\S]+)\]$/;

/** The four fields inside a structure token. */
export function structureFields(inner: string):
{ smiles: string; name: string; arrows: string; annotations: string } {
  const [smiles = '', name = '', arrows = '', annotations = ''] = inner.split('|');
  return { smiles, name, arrows, annotations };
}

/**
 * A structure token, written out. THE ONLY PLACE THAT DECIDES ITS SHAPE.
 *
 * The token layer and the document layer each used to spell this themselves, which is two
 * copies of a positional grammar that has grown a field twice. They call this now, so a field
 * added here cannot reach one writer and miss the other.
 */
export function writeStructureToken(f: {
  smiles: string; name: string; arrows?: string; annotations?: string;
}): string {
  /* Trailing empties are dropped and interior ones are kept, because the fields are read by
     POSITION — `[smiles:O||n1=x]` says "no arrows, these annotations", and dropping that empty
     third field would read the annotations as arrows. */
  const fields = [f.smiles, f.name, f.arrows ?? '', f.annotations ?? ''];
  while (fields.length > 2 && !fields[fields.length - 1]) fields.pop();
  return `[smiles:${fields.join('|')}]`;
}

const OPENERS = '{[(';
const CLOSERS = '}])';

/**
 * How deeply nested an offset into a token is.
 *
 * The one rule this file has — that a space is a token boundary only at depth zero — has to be
 * answerable mid-token too, because the row needs it to decide what the space bar does. A label
 * like `->[conc. H2SO4]` has a space in it and is still one arrow.
 *
 * An offset inside a token whose brackets never balanced reads as zero: such a token is already
 * being held whole by `tokenize`, and guessing at a depth for it would be worse than treating a
 * space there as the ordinary boundary.
 */
export function depthAt(text: string, index: number): number {
  let depth = 0;
  for (let i = 0; i < index && i < text.length; i++) {
    const ch = text[i]!;
    if (OPENERS.includes(ch)) depth++;
    else if (CLOSERS.includes(ch)) depth--;
  }
  return Math.max(0, depth);
}

/**
 * Split on spaces at bracket depth zero — mhchem's rule, and the same one the renderer uses to
 * decide where a reaction may wrap. Unbalanced brackets return null so the caller can keep the
 * source whole rather than guess at it.
 */
export function splitAtDepthZero(source: string): string[] | null {
  const out: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of source) {
    if (OPENERS.includes(ch)) depth++;
    else if (CLOSERS.includes(ch)) depth--;
    if (depth < 0) return null;
    if (ch === ' ' && depth === 0) {
      if (current) { out.push(current); current = ''; }
      continue;
    }
    current += ch;
  }
  if (depth !== 0) return null;
  if (current) out.push(current);
  return out;
}

/**
 * The source a teacher's reaction is stored as, read as the row they will edit.
 *
 * An unparseable source becomes ONE token holding all of it, so a teacher never loses what they
 * wrote to a bracket they have not finished typing.
 */
export function tokenize(source: string): Token[] {
  const parts = splitAtDepthZero(source);
  if (parts === null) return source ? [{ text: source }] : [];
  return parts.map(part => {
    /* MARKS FIRST, then the structure. The other way round, a highlighted molecule —
       `[#hex:[smiles:c1ccccc1|benzene]]` — failed the structure test on its leading `[#`, fell
       through to the text path, and the drawing became its own source in a coloured box. */
    const { text, marks } = parseMarks(part);
    /* The arc, before the text path: it is Agavi syntax and `toDisplayMapped` would read its
       insides as chemistry and hand back something no one typed. */
    const arc = readCoSubstrate(text, 0);
    if (arc && arc.end === text.length) {
      const { arrow, into, out } = arc;
      return marks.length
        ? { text, cosub: { arrow, into, out }, marks }
        : { text, cosub: { arrow, into, out } };
    }
    const wrapped = STRUCTURE_WRAP.exec(text);
    if (wrapped) {
      const { smiles, name, arrows, annotations } = structureFields(wrapped[1]!);
      const made = { name, ...(arrows ? { arrows } : {}), ...(annotations ? { annotations } : {}) };
      return marks.length
        ? { text: smiles, structure: made, marks }
        : { text: smiles, structure: made };
    }
    const shown = toDisplayMapped(text);
    if (!marks.length) return { text: shown.text };
    const moved = marks.map(m => ({
      ...m,
      start: shown.map[Math.min(m.start, shown.map.length - 1)]!,
      end: shown.map[Math.min(m.end, shown.map.length - 1)]!,
    }));
    return { text: shown.text, marks: moved.filter(m => m.end > m.start) };
  });
}

/** Write one token back out, wrapping every mark around the characters it was put on. */
export function serializeToken(token: Token): string {
  /* The arc writes itself, from the module that reads it. */
  if (token.cosub) return writeCoSubstrate(token.cosub);
  /* A structure carries no marks yet — marking a group inside a drawing is a different
     mechanism from marking a range of text, and waits on the picking surface. */
  if (token.structure) {
    const drawn = writeStructureToken({
      smiles: token.text,
      name: token.structure.name,
      arrows: token.structure.arrows,
      annotations: token.structure.annotations,
    });
    /* A highlight on a drawing wraps the whole thing: there is no range inside a picture to put
       it on, which is the same reason the structure is an atom in the editor. */
    const colour = token.marks?.find(m => m.kind === 'color');
    return colour ? `[${colour.value}:${drawn}]` : drawn;
  }
  /* `text` is what the teacher sees; mhchem is produced here, at the boundary — and the marks
     come with it, moved onto the offsets the source actually has. */
  const { text, map } = toSourceMapped(token.text);
  const marks = (token.marks ?? []).map(m => ({
    ...m,
    start: map[Math.min(m.start, map.length - 1)]!,
    end: map[Math.min(m.end, map.length - 1)]!,
  }));
  return serializeMarks(text, marks);
}

/** Build a structure token. The name is what gets spoken, so it is not optional. */
export function structureToken(smiles: string, name: string): Token {
  return { text: smiles.trim(), structure: { name: name.trim() } };
}

/** The row, back as the mhchem source everything else in the app already reads. */
export function serialize(tokens: Token[]): string {
  return tokens.map(serializeToken).filter(t => t.length > 0).join(' ');
}

/** Mark a range of a token, snapping the ends to somewhere the notation survives. */
export function markToken(
  token: Token, start: number, end: number, kind: MarkKind, value?: string,
): Token {
  const [a, b] = snapRange(token.text, start, end);
  return { text: token.text, marks: applyMark(token.marks ?? [], a, b, kind, value) };
}

/** Take a kind of mark off a range, over the same snapped bounds that put it there. */
export function unmarkToken(token: Token, start: number, end: number, kind: MarkKind): Token {
  const [a, b] = snapRange(token.text, start, end);
  return { text: token.text, marks: clearMark(token.marks ?? [], a, b, kind) };
}

/** Replace a run of a token's text, carrying the marks along with the characters they were on. */
export function replaceRange(token: Token, start: number, end: number, insert: string): Token {
  const [a, b] = start <= end ? [start, end] : [end, start];
  const marks = shiftMarks(token.marks ?? [], a, b, insert.length);
  return {
    text: token.text.slice(0, a) + insert + token.text.slice(b),
    ...(marks.length ? { marks } : {}),
  };
}

/**
 * Join two tokens, as a Backspace at the start of the second one does.
 *
 * Two different colours cannot both survive into one token, and picking one would repaint the
 * other half without being asked. So they cancel: a merge of disagreeing colours is uncoloured,
 * and the teacher re-applies the one they want to the token they now have.
 */
export function mergeTokens(left: Token, right: Token): Token {
  const text = left.text + right.text;
  if (!left.text) return { ...right, text };
  if (!right.text) return { ...left, text };
  /* Each side keeps the marks it had, the right-hand ones moved along by the length of the left.
     Nothing cancels any more: two differently coloured halves used to fight over a single
     token-wide colour and both lost, where now they simply sit side by side. */
  const shifted = (right.marks ?? []).map(m =>
    ({ ...m, start: m.start + left.text.length, end: m.end + left.text.length }));
  /* Tidied, or two halves that were the same colour come back as two marks meeting in the
     middle instead of the single span they look like. */
  const marks = tidyMarks([...(left.marks ?? []), ...shifted]);
  return marks.length ? { text, marks } : { text };
}

/** Cut a token in two at an offset, as typing a space inside one does. Both halves keep it. */
export function splitToken(token: Token, at: number): [Token, Token] {
  const marks = token.marks ?? [];
  const left = sliceMarks(marks, 0, at);
  const right = sliceMarks(marks, at, token.text.length);
  return [
    { text: token.text.slice(0, at), ...(left.length ? { marks: left } : {}) },
    { text: token.text.slice(at), ...(right.length ? { marks: right } : {}) },
  ];
}
